import * as _ from 'lodash';
import {
    AutoRestockArticle,
    AutoRestockProduct,
    BrandStock,
    InitialDelivery,
    InitialDeliveryState,
    InitialDeliveryStatus,
    Suggestion
} from 'app/smart-replenishment/types/shared';
import { SeasonKey } from './season';

export function calculateTotalTargetQuantity(article: AutoRestockArticle): number {
    if (!article?.sizes?.length) {
        return 0;
    }

    return article.sizes.reduce((total, size) => {
        if (!size.autoRestock) {
            return total;
        }

        const storeQuantities = Object.values(size.autoRestock).reduce(
            (storeTotal, restockData) => storeTotal + (restockData.targetStock || 0),
            0
        );

        return total + storeQuantities;
    }, 0);
}

export function calculatePerStoreTargetQuantity(article: AutoRestockArticle): { [gln: string]: number } {
    const perStoreTotals: { [gln: string]: number } = {};

    if (!article?.sizes?.length) {
        return perStoreTotals;
    }

    for (const size of article.sizes) {
        if (size.autoRestock) {
            for (const [gln, restockData] of Object.entries(size.autoRestock)) {
                if (restockData.targetStock !== undefined) {
                    perStoreTotals[gln] = (perStoreTotals[gln] || 0) + restockData.targetStock;
                }
            }
        }
    }

    return perStoreTotals;
}

export function mapSuggestionBrandStock(suggestion: Suggestion) {
    if (_.isNil(suggestion?.articles)) {
        return suggestion;
    }

    return {
        ...suggestion,
        articles: suggestion.articles.map((article) => ({
            ...article,
            products: article.products?.map((product) => ({
                ...product,
                brandStock: mapBrandStockToObject(product.brandStock)
            }))
        }))
    };
}

/**
 * Coerces brandStock values (numbers or object) into a unified object shape with backorder info
 * without mutating the original reference.
 */
export function mapBrandStockToObject(brandStock: BrandStockInput): BrandStock {
    if (_.isNil(brandStock)) {
        return { available: 0 };
    }

    if (typeof brandStock === 'number') {
        return {
            available: brandStock
        };
    }

    return brandStock;
}

export function getBrandStockAvailableQuantity(brandStock: BrandStockInput): number {
    const normalizedBrandStock = mapBrandStockToObject(brandStock);
    return (normalizedBrandStock.available || 0) + (normalizedBrandStock.backorder?.available || 0);
}

export function getArticleLabel<
    T extends {
        articleNumber?: string;
        styleGroupId?: string;
        colorGroupId: string;
        colorName?: string;
    }
>(article: T): string {
    const articleId = article.articleNumber || article.styleGroupId;

    if (!articleId || !article.colorGroupId) {
        return '';
    }

    return `${articleId} / ${article.colorGroupId}${article.colorName ? `-${article.colorName}` : ''}`;
}

export function parseArticleHash(articleHash: string): { styleGroupId: string; colorGroupId: string } {
    const separatorIndex = articleHash.indexOf('#');
    return {
        styleGroupId: articleHash.slice(0, separatorIndex),
        colorGroupId: articleHash.slice(separatorIndex + 1)
    };
}

export function getReplenishmentSchedule(
    products: AutoRestockProduct[],
    gln: string
): {
    startDate: string;
    endDate: string;
    isActive: boolean;
    initialDelivery: InitialDeliveryState;
} | null {
    const autoRestockSettings = products?.[0]?.autoRestock?.[gln];

    if (!autoRestockSettings) {
        return null;
    }

    return {
        startDate: autoRestockSettings.startDate,
        endDate: autoRestockSettings.endDate,
        isActive: autoRestockSettings.isActive ?? true,
        initialDelivery: getArticleInitialDeliveryState(products, gln)
    };
}

export function getArticleInitialDeliveryState(products: AutoRestockProduct[], gln: string): InitialDeliveryState {
    const sizesWithTargetStock = products.filter((product) => (product.autoRestock?.[gln]?.targetStock ?? 0) > 0);
    const sizesToRollUp =
        sizesWithTargetStock.length > 0
            ? sizesWithTargetStock
            : products.filter((product) => product.autoRestock?.[gln]);

    const eligibleSizes = sizesToRollUp.map((product) => ({
        size: product.size,
        status: product.autoRestock[gln].initialDelivery?.status
    }));

    if (eligibleSizes.length === 0) {
        return { status: InitialDeliveryStatus.Disabled };
    }

    const deliveredCount = eligibleSizes.filter(({ status }) => status === InitialDeliveryStatus.Delivered).length;
    const awaitingSizes = eligibleSizes
        .filter(({ status }) => status === InitialDeliveryStatus.Enabled)
        .map(({ size }) => size);

    if (deliveredCount === eligibleSizes.length) {
        return { status: InitialDeliveryStatus.Delivered };
    }

    if (deliveredCount > 0) {
        return {
            status: InitialDeliveryStatus.PartiallyDelivered,
            receivedCount: deliveredCount,
            totalCount: eligibleSizes.length,
            awaitingSizes
        };
    }

    if (awaitingSizes.length > 0) {
        return { status: InitialDeliveryStatus.Enabled };
    }

    return { status: InitialDeliveryStatus.Disabled };
}

export function isAwaitingInitialDelivery(initialDelivery: InitialDeliveryState | undefined): boolean {
    return (
        initialDelivery?.status === InitialDeliveryStatus.Enabled ||
        (initialDelivery?.status === InitialDeliveryStatus.PartiallyDelivered &&
            (initialDelivery.awaitingSizes?.length ?? 0) > 0)
    );
}

export function getInitialDeliveryControlStatus(
    initialDelivery: InitialDeliveryState | undefined
): InitialDeliveryStatus {
    if (
        initialDelivery?.status === InitialDeliveryStatus.PartiallyDelivered &&
        (initialDelivery.awaitingSizes?.length ?? 0) === 0
    ) {
        return InitialDeliveryStatus.Disabled;
    }

    return initialDelivery?.status ?? InitialDeliveryStatus.Disabled;
}

export function isUserSettableInitialDeliveryStatus(
    status: InitialDeliveryStatus | undefined | null
): status is InitialDeliveryStatus.Enabled | InitialDeliveryStatus.Disabled {
    return status === InitialDeliveryStatus.Enabled || status === InitialDeliveryStatus.Disabled;
}

export function shouldShowInitialDeliveryControl(status: InitialDeliveryStatus | undefined | null): boolean {
    return isUserSettableInitialDeliveryStatus(status) || status === InitialDeliveryStatus.PartiallyDelivered;
}

export function isStartDateLockedByInitialDelivery(status: InitialDeliveryStatus | undefined | null): boolean {
    return status === InitialDeliveryStatus.Enabled || status === InitialDeliveryStatus.PartiallyDelivered;
}

export function shouldPreserveExistingInitialDelivery(
    formStatus: InitialDeliveryStatus | undefined | null,
    hasExplicitControlChange: boolean
): boolean {
    if (formStatus === InitialDeliveryStatus.Delivered || formStatus === InitialDeliveryStatus.PartiallyDelivered) {
        return true;
    }

    return formStatus === InitialDeliveryStatus.Disabled && !hasExplicitControlChange;
}

export function getWritableInitialDeliveryStatus(
    scheduleStatus: InitialDeliveryStatus | undefined,
    perSizeStatus: InitialDeliveryStatus | undefined
): InitialDeliveryStatus {
    if (perSizeStatus === InitialDeliveryStatus.Delivered) {
        return InitialDeliveryStatus.Delivered;
    }

    if (isUserSettableInitialDeliveryStatus(scheduleStatus)) {
        return scheduleStatus;
    }

    return perSizeStatus ?? InitialDeliveryStatus.Disabled;
}

/**
 * While initial delivery is Enabled the article waits for its first delivery, so the start date has no effect
 * and is ignored when the status is derived.
 */
export function getReplenishmentStatus(
    startDate: string | null,
    endDate: string | null,
    isActive: boolean,
    initialDeliveryStatus?: InitialDeliveryStatus | null
): ArticleStatus {
    if (!isActive) {
        return ArticleStatus.Inactive;
    }

    const ignoreStartDate = initialDeliveryStatus === InitialDeliveryStatus.Enabled;
    const start = startDate && !ignoreStartDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    const now = new Date();

    const startStatus = start ? (start <= now ? DateStatus.PAST : DateStatus.FUTURE) : DateStatus.UNSPECIFIED;
    const endStatus = end ? (end <= now ? DateStatus.PAST : DateStatus.FUTURE) : DateStatus.UNSPECIFIED;

    switch (startStatus + endStatus) {
        case DateStatus.UNSPECIFIED + DateStatus.UNSPECIFIED:
            return ArticleStatus.Active;
        case DateStatus.PAST + DateStatus.PAST:
            return ArticleStatus.Expired;
        case DateStatus.PAST + DateStatus.FUTURE:
            return ArticleStatus.ActiveUntil;
        case DateStatus.PAST + DateStatus.UNSPECIFIED:
            return ArticleStatus.Active;
        case DateStatus.FUTURE + DateStatus.PAST:
            return ArticleStatus.Error;
        case DateStatus.FUTURE + DateStatus.FUTURE:
            return ArticleStatus.Scheduled;
        case DateStatus.FUTURE + DateStatus.UNSPECIFIED:
            return ArticleStatus.Scheduled;
        case DateStatus.UNSPECIFIED + DateStatus.FUTURE:
            return ArticleStatus.ActiveUntil;
        case DateStatus.UNSPECIFIED + DateStatus.PAST:
            return ArticleStatus.Expired;
        default:
            return ArticleStatus.Inactive;
    }
}

export function getInitialDeliveryStatusFromSettings(
    initialDelivery: InitialDelivery | undefined,
    articleSeason: { seasonKey?: string; seasonYearKey?: string }
): InitialDeliveryStatus.Enabled | InitialDeliveryStatus.Disabled {
    const isEnabled = isInitialDeliveryEnabledForSeason(initialDelivery, articleSeason);
    return isEnabled ? InitialDeliveryStatus.Enabled : InitialDeliveryStatus.Disabled;
}

function isInitialDeliveryEnabledForSeason(
    initialDelivery: InitialDelivery | undefined,
    articleSeason: { seasonKey?: string; seasonYearKey?: string }
): boolean {
    if (!initialDelivery?.enabled || !articleSeason.seasonKey) {
        return false;
    }

    return initialDelivery.seasons.some((season) => {
        if (season.key !== articleSeason.seasonKey) {
            return false;
        }

        if (season.key === SeasonKey.NOS) {
            return true;
        }

        return season.year === articleSeason.seasonYearKey;
    });
}

type BrandStockInput = number | BrandStock;

enum DateStatus {
    FUTURE = 'FUTURE',
    PAST = 'PAST',
    UNSPECIFIED = 'UNSPECIFIED'
}

export enum ArticleStatus {
    Active = 'Active',
    ActiveUntil = 'ActiveUntil',
    Scheduled = 'Scheduled',
    Inactive = 'Inactive',
    Expired = 'Expired',
    Error = 'Error'
}

export function getArticleStatusBuckets(status: ArticleStatus): string[] {
    switch (status) {
        case ArticleStatus.Inactive:
            return ['inactive'];
        case ArticleStatus.Expired:
            return ['expired'];
        case ArticleStatus.Scheduled:
            return ['scheduled'];
        case ArticleStatus.Active:
        case ArticleStatus.ActiveUntil:
            return ['active'];
        case ArticleStatus.Error:
            // Treat error conservatively as expired, so it can be surfaced with Expired filter.
            return ['expired'];
        default:
            return [];
    }
}
